import * as React from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useHistory } from "react-router-dom";
import { ReportJob, ReportJobCollection, MetricsService, ReportJobStatus, ReportJobType } from "gen/clients";
import { DataTable } from "components/DataTable/DataTable";
import { DataTableRowAction } from "components/DataTable/types";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { TokenBasedPaginator } from "components/TokenBasedPaginator/TokenBasedPaginator";
import { ConfirmationDialog } from "components/ConfirmationDialog/ConfirmationDialog";
import { Chip, LinearProgress, Box, Typography } from "@mui/material";
import { formatDate } from "utils/dateUtils";

interface MtUsageReportsListProps {
  onRunningReportsChange?: (hasRunningReports: boolean) => void;
}

const MtUsageReportsList: React.FC<MtUsageReportsListProps> = ({ onRunningReportsChange }) => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(10);
  const [nextPageToken, setNextPageToken] = React.useState<string>();
  const [pageToken, setPageToken] = React.useState<string>();
  const paginatorResetRef = React.useRef<() => void>(() => {
    window.console.log("placeholder function");
  });

  const [cancelDialogOpen, setCancelDialogOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedReport, setSelectedReport] = React.useState<ReportJob | null>(null);

  const { data, isLoading, error } = useQuery<ReportJobCollection>(
    ["listClientMtUsageReports", pageToken, rowsPerPage],
    {
      queryFn: () =>
        MetricsService.listReportJobs({
          type: ReportJobType.CLIENT_MT_USAGE,
          pageToken,
          pageSize: rowsPerPage
        }),
      onSuccess: collection => {
        setNextPageToken(collection.nextPageToken);
        const hasRunningReports = collection.items.some(
          report => report.status === ReportJobStatus.PENDING || report.status === ReportJobStatus.RUNNING
        );
        onRunningReportsChange?.(hasRunningReports);
      },
      refetchInterval: data => {
        const hasRunningReports = data?.items.some(
          report => report.status === ReportJobStatus.PENDING || report.status === ReportJobStatus.RUNNING
        );
        return hasRunningReports ? 5000 : false;
      }
    }
  );

  const {
    mutate: cancelReport,
    isLoading: isCancelling,
    error: cancelError
  } = useMutation(MetricsService.cancelReportJob, {
    onSuccess: () => {
      queryClient.invalidateQueries(["listClientMtUsageReports"]);
      setCancelDialogOpen(false);
      setSelectedReport(null);
    }
  });

  const {
    mutate: deleteReport,
    isLoading: isDeleting,
    error: deleteError
  } = useMutation(MetricsService.deleteReportJob, {
    onSuccess: () => {
      queryClient.invalidateQueries(["listClientMtUsageReports"]);
      setDeleteDialogOpen(false);
      setSelectedReport(null);
    }
  });

  const onRowsPerPageChange = React.useCallback((newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPageToken(undefined);
    setNextPageToken(undefined);
    paginatorResetRef?.current();
  }, []);

  const handleRowClick = React.useCallback(
    (report: ReportJob) => {
      if (report.status === ReportJobStatus.COMPLETED) {
        history.push(`/analytics/mtUsageForClientBilling/reports/${report.id}`);
      }
    },
    [history]
  );

  const getStatusChip = React.useCallback((status: ReportJobStatus) => {
    switch (status) {
      case ReportJobStatus.PENDING:
        return <Chip label="Pending" color="default" size="small" />;
      case ReportJobStatus.RUNNING:
        return <Chip label="Running" color="info" size="small" />;
      case ReportJobStatus.COMPLETED:
        return <Chip label="Completed" color="success" size="small" />;
      case ReportJobStatus.FAILED:
        return <Chip label="Failed" color="error" size="small" />;
      case ReportJobStatus.CANCELLED:
        return <Chip label="Cancelled" color="warning" size="small" />;
      default:
        return <Chip label={status} color="default" size="small" />;
    }
  }, []);

  const getProgressCell = React.useCallback((report: ReportJob) => {
    if (report.status === ReportJobStatus.RUNNING && report.progress) {
      const { current = 0, total = 0, percentage = 0 } = report.progress;
      return (
        <Box sx={{ width: "100%" }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Box sx={{ width: "100%", mr: 1 }}>
              <LinearProgress variant="determinate" value={percentage} />
            </Box>
            <Box sx={{ minWidth: 35 }}>
              <Typography variant="body2" color="text.secondary">
                {Math.round(percentage)}%
              </Typography>
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {current} / {total} items processed
          </Typography>
        </Box>
      );
    }
    return null;
  }, []);

  const getParamsCell = React.useCallback((report: ReportJob) => {
    if (!report.params) {
      return "-";
    }
    const parts: string[] = [];
    if (report.params.fromDate) {
      parts.push(`From: ${formatDate(report.params.fromDate)}`);
    }
    if (report.params.toDate) {
      parts.push(`To: ${formatDate(report.params.toDate)}`);
    }
    if (report.params.billingType) {
      parts.push(`Type: ${report.params.billingType}`);
    }
    return parts.join(", ") || "-";
  }, []);

  const handleCancelClick = React.useCallback((report: ReportJob) => {
    setSelectedReport(report);
    setCancelDialogOpen(true);
  }, []);

  const handleDeleteClick = React.useCallback((report: ReportJob) => {
    setSelectedReport(report);
    setDeleteDialogOpen(true);
  }, []);

  const closeCancelDialog = React.useCallback(() => {
    setCancelDialogOpen(false);
    setSelectedReport(null);
  }, []);

  const closeDeleteDialog = React.useCallback(() => {
    setDeleteDialogOpen(false);
    setSelectedReport(null);
  }, []);

  const confirmCancel = React.useCallback(() => {
    if (selectedReport) {
      cancelReport({ reportId: selectedReport.id });
    }
  }, [cancelReport, selectedReport]);

  const confirmDelete = React.useCallback(() => {
    if (selectedReport) {
      deleteReport({ reportId: selectedReport.id });
    }
  }, [deleteReport, selectedReport]);

  const getRowActions = React.useCallback(
    (report: ReportJob): DataTableRowAction[] => {
      const actions: DataTableRowAction[] = [];

      if (report.status === ReportJobStatus.PENDING || report.status === ReportJobStatus.RUNNING) {
        actions.push({
          title: "Cancel",
          action: () => handleCancelClick(report),
          color: "warning"
        });
      }

      actions.push({
        title: "Delete",
        action: () => handleDeleteClick(report),
        color: "error"
      });

      return actions;
    },
    [handleCancelClick, handleDeleteClick]
  );

  return (
    <>
      <DataTable
        isLoading={isLoading}
        error={!!error && <ApiErrorMessage apiError={error} />}
        columns={[
          {
            id: "id",
            title: "Report ID",
            cell: (report: ReportJob) => report.id
          },
          {
            id: "status",
            title: "Status",
            cell: (report: ReportJob) => getStatusChip(report.status)
          },
          {
            id: "progress",
            title: "Progress",
            cell: getProgressCell
          },
          {
            id: "params",
            title: "Parameters",
            cell: getParamsCell
          },
          {
            id: "createdBy",
            title: "Created By",
            cell: (report: ReportJob) => report.createdBy
          },
          {
            id: "createdAt",
            title: "Created At",
            cell: (report: ReportJob) => formatDate(report.createdAt)
          },
          {
            id: "completedAt",
            title: "Completed At",
            cell: (report: ReportJob) => (report.completedAt ? formatDate(report.completedAt) : "-")
          }
        ]}
        data={data?.items || []}
        rowKey={(report: ReportJob) => report.id}
        onRowClick={handleRowClick}
        rowActions={getRowActions}
        dense={true}
      />
      <TokenBasedPaginator
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        onPageTokenChange={setPageToken}
        nextPageToken={nextPageToken}
        currentPageDataRows={data?.items.length}
        rowsPerPageOptions={[10, 20, 50, 100]}
        resetRef={paginatorResetRef}
      />

      {cancelDialogOpen && selectedReport && (
        <ConfirmationDialog
          title="Cancel Report"
          content={
            <>
              Are you sure you want to cancel the report &quot;{selectedReport.id}&quot;? This action will stop the
              report generation process.
            </>
          }
          onClose={closeCancelDialog}
          onConfirm={confirmCancel}
          confirmationButtonColor="warning"
          isLoading={isCancelling}
          error={cancelError ? <ApiErrorMessage apiError={cancelError} /> : undefined}
        />
      )}

      {deleteDialogOpen && selectedReport && (
        <ConfirmationDialog
          title="Delete Report"
          content={
            <>
              Are you sure you want to delete the report &quot;{selectedReport.id}&quot;? This action cannot be undone
              and will permanently remove the report and all associated files.
            </>
          }
          onClose={closeDeleteDialog}
          onConfirm={confirmDelete}
          confirmationButtonColor="error"
          isLoading={isDeleting}
          error={deleteError ? <ApiErrorMessage apiError={deleteError} /> : undefined}
        />
      )}
    </>
  );
};

export { MtUsageReportsList };
